import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    Handle,
    Position,
    useNodesState,
    useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// n8n-style flow board for the complaint lifecycle, rendered with React Flow (pan / zoom / drag /
// minimap). Node + edge geometry is derived from GET /admin/aduan-flow; counts poll every 15s and
// pulse on delta, with an optional sound cue. Dark neon (JAIS navy + gold) theme, fullscreen mode.

const POLL_MS = 15000;
const NODE_H = 96;
const VIEW_H = 560;

const SOURCE_META = {
    telegram: { icon: 'bi-telegram' },
    whatsapp: { icon: 'bi-whatsapp' },
    portal: { icon: 'bi-globe2' },
    kaunter: { icon: 'bi-building' },
    import: { icon: 'bi-box-arrow-in-down' },
    lain: { icon: 'bi-three-dots' },
};

const STAGE_META = {
    baru: { icon: 'bi-inboxes', x: 450, y: 232, href: '/app/complaints' },
    tunggu_pengesahan: { icon: 'bi-patch-question', x: 710, y: 232, href: '/app/complaints/pending-approval' },
    disahkan: { icon: 'bi-patch-check', x: 970, y: 232, href: '/app/complaints' },
    dihantar_ke_daerah: { icon: 'bi-send', x: 1230, y: 232, href: '/app/complaints' },
    dalam_tindakan: { icon: 'bi-person-badge', x: 1490, y: 232, href: '/app/complaints/my-pic' },
    kiv: { icon: 'bi-pause-circle', x: 1490, y: 432, href: '/app/complaints' },
    laporan_tindakan: { icon: 'bi-file-earmark-text', x: 1750, y: 232, href: '/app/complaints' },
};

const DOWNSTREAM_META = {
    kes: { icon: 'bi-folder2-open', x: 2010, y: 232, variant: 'kes', href: '/app/complaints' },
    iwaran: { icon: 'bi-file-earmark-ruled', x: 2010, y: 56, variant: 'loose', href: '/app/i-waran' },
    arahan_beredar: { icon: 'bi-signpost-2', x: 2010, y: 432, variant: 'loose', href: '/app/arahan-beredar' },
};

// Pick edge anchor handles: horizontal (right -> left) when the target sits clearly to the right,
// otherwise vertical (bottom -> top). Mirrors the original hand-rolled routing.
function handlesFor(from, to) {
    if (to.x > from.x + 60) return { sourceHandle: 'R-s', targetHandle: 'L-t' };
    return { sourceHandle: 'B-s', targetHandle: 'T-t' };
}

// Colour the minimap dots by node role.
function miniColor(node) {
    const v = node?.data?.variant;
    if (v === 'source') return '#22d3ee';
    if (v === 'kes') return '#ffd164';
    if (v === 'final') return '#4ade9e';
    if (v === 'kiv') return '#f6b44b';
    if (v === 'email') return node?.data?.failed > 0 ? '#f87171' : '#34d399';
    if (v === 'loose') return '#64748b';
    return '#60a5fa';
}

// Custom node: the neon card, plus four (invisible, non-connectable) handles used only as edge anchors.
function FlowNode({ data: n }) {
    return (
        <div className={`afn afn-${n.variant}${n.pulse ? ' afn-pulse' : ''}${n.isEmail && n.failed > 0 ? ' afn-email-bad' : ''}`}>
            <Handle type="target" position={Position.Left} id="L-t" isConnectable={false} className="af-h" />
            <Handle type="target" position={Position.Top} id="T-t" isConnectable={false} className="af-h" />
            <Handle type="source" position={Position.Right} id="R-s" isConnectable={false} className="af-h" />
            <Handle type="source" position={Position.Bottom} id="B-s" isConnectable={false} className="af-h" />
            <div className="afn-row">
                <span className="afn-ico"><i className={`bi ${n.icon}`}></i></span>
                <span className="afn-label">{n.label}</span>
            </div>
            {n.isEmail ? (
                <>
                    <div className="afn-email-stats">
                        <div className="afn-em afn-em-ok"><i className="bi bi-check-circle-fill"></i><span>{n.sent}</span></div>
                        <div className="afn-em afn-em-bad"><i className="bi bi-x-circle-fill"></i><span>{n.failed}</span></div>
                    </div>
                    <div className="afn-email-rate">
                        {n.rate === null || n.rate === undefined ? 'tiada hantaran' : `${n.rate}% berjaya`}
                    </div>
                </>
            ) : (
                <div className="afn-count">{n.count}</div>
            )}
            {n.loose && <div className="afn-tag">longgar</div>}
            {n.byDistrict && n.byDistrict.length > 0 && <div className="afn-tag afn-tag-dist">daerah</div>}
        </div>
    );
}

const nodeTypes = { flow: FlowNode };

// Translate an /admin/aduan-flow payload into React Flow nodes + edges.
function buildGraph(data, pulseSet) {
    const nodes = [];
    const pos = {};
    const add = (id, x, y, nodeData) => {
        pos[id] = { x, y };
        nodes.push({ id, type: 'flow', position: { x, y }, data: nodeData, draggable: true });
    };

    const sources = data.sources || [];
    const startY = VIEW_H / 2 - NODE_H / 2 - ((sources.length - 1) * 100) / 2;
    sources.forEach((s, i) => {
        const id = `src_${s.key}`;
        add(id, 40, Math.round(startY + i * 100), {
            variant: 'source', label: s.label, count: s.count,
            icon: (SOURCE_META[s.key] || {}).icon || 'bi-dot', pulse: Boolean(pulseSet[id]),
        });
    });

    (data.stages || []).forEach((st) => {
        const meta = STAGE_META[st.code];
        if (!meta) return;
        const id = `st_${st.code}`;
        add(id, meta.x, meta.y, {
            variant: st.code === 'laporan_tindakan' ? 'final' : st.code === 'kiv' ? 'kiv' : 'stage',
            label: st.label, count: st.count, icon: meta.icon, href: meta.href,
            pulse: Boolean(pulseSet[id]),
            byDistrict: Array.isArray(st.by_district) ? st.by_district : null,
        });
    });

    Object.entries(data.downstream || {}).forEach(([k, v]) => {
        const meta = DOWNSTREAM_META[k];
        if (!meta) return;
        const id = `dn_${k}`;
        add(id, meta.x, meta.y, {
            variant: meta.variant, label: v.label, count: v.count, icon: meta.icon,
            loose: v.linked === false, href: meta.href, pulse: Boolean(pulseSet[id]),
        });
    });

    if (data.email) {
        const e = data.email;
        add('email', 1230, 432, {
            variant: 'email', label: 'E-mel Dispatch', icon: 'bi-envelope-paper',
            sent: e.sent || 0, failed: e.failed || 0, skipped: e.skipped || 0,
            rate: e.success_rate, today: e.today || { sent: 0, failed: 0 },
            count: (e.sent || 0) + (e.failed || 0),
            isEmail: true, href: '/app/email-log',
            pulse: Boolean(pulseSet.email_sent || pulseSet.email_failed),
        });
    }

    const defs = [];
    sources.forEach((s) => defs.push({ from: `src_${s.key}`, to: 'st_baru', color: '#22d3ee', intake: true }));
    [
        ['st_baru', 'st_tunggu_pengesahan'], ['st_tunggu_pengesahan', 'st_disahkan'],
        ['st_disahkan', 'st_dihantar_ke_daerah'], ['st_dihantar_ke_daerah', 'st_dalam_tindakan'],
        ['st_dalam_tindakan', 'st_laporan_tindakan'], ['st_laporan_tindakan', 'dn_kes'],
    ].forEach(([from, to]) => defs.push({ from, to, color: '#60a5fa' }));
    defs.push({ from: 'st_dalam_tindakan', to: 'st_kiv', color: '#f6b44b', static: true });
    defs.push({ from: 'st_dalam_tindakan', to: 'dn_iwaran', color: '#94a3b8', loose: true });
    defs.push({ from: 'dn_kes', to: 'dn_arahan_beredar', color: '#94a3b8', loose: true });
    if (data.email) {
        const healthy = (data.email.failed || 0) === 0;
        defs.push({ from: 'st_dihantar_ke_daerah', to: 'email', color: healthy ? '#34d399' : '#f87171' });
    }

    const edges = defs
        .filter((e) => pos[e.from] && pos[e.to])
        .map((e, i) => {
            const h = handlesFor(pos[e.from], pos[e.to]);
            const animated = !e.loose && !e.static;
            return {
                id: `e${i}`,
                source: e.from,
                target: e.to,
                sourceHandle: h.sourceHandle,
                targetHandle: h.targetHandle,
                type: 'default',
                animated,
                className: `af-rf-edge${e.loose ? ' af-rf-edge-loose' : ''}${e.intake ? ' af-rf-edge-intake' : ''}`,
                style: {
                    stroke: e.color,
                    strokeWidth: e.loose ? 1.8 : 2.4,
                    ...(e.loose ? {} : { filter: `drop-shadow(0 0 3px ${e.color})` }),
                },
            };
        });

    return { nodes, edges };
}

export default function AduanFlowDashboard() {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const containerRef = useRef(null);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [hasData, setHasData] = useState(false);
    const [totals, setTotals] = useState(null);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const prevCounts = useRef({});

    const [soundOn, setSoundOn] = useState(() => {
        try { return localStorage.getItem('af_sound') !== '0'; } catch { return true; }
    });
    const soundOnRef = useRef(soundOn);
    const audioCtxRef = useRef(null);

    useEffect(() => {
        soundOnRef.current = soundOn;
        try { localStorage.setItem('af_sound', soundOn ? '1' : '0'); } catch { /* ignore */ }
    }, [soundOn]);

    // Lazily create / resume a WebAudio context (needs a user gesture to start on most browsers).
    const ensureAudio = useCallback(() => {
        try {
            if (!audioCtxRef.current) {
                const AC = window.AudioContext || window.webkitAudioContext;
                if (AC) audioCtxRef.current = new AC();
            }
            if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
                audioCtxRef.current.resume();
            }
        } catch { /* ignore */ }
        return audioCtxRef.current;
    }, []);

    // Short synthesized blip. alert=true → a lower two-note tone (used when a failure count rises).
    const playBeep = useCallback((alert) => {
        const ctx = audioCtxRef.current;
        if (!ctx) return;
        try {
            const now = ctx.currentTime;
            const freqs = alert ? [392, 294] : [880];
            const peak = alert ? 0.22 : 0.13;
            freqs.forEach((f, i) => {
                const t = now + i * 0.15;
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = f;
                gain.gain.setValueAtTime(0.0001, t);
                gain.gain.exponentialRampToValueAtTime(peak, t + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(t);
                osc.stop(t + 0.24);
            });
        } catch { /* ignore */ }
    }, []);

    useEffect(() => () => { try { audioCtxRef.current?.close(); } catch { /* ignore */ } }, []);

    const load = useCallback(() => {
        if (!apiUrl) {
            setError('API URL tidak diset.');
            return;
        }
        axios
            .get(`${apiUrl}/admin/aduan-flow`, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            })
            .then((res) => {
                const d = res.data || {};
                const next = {};
                const pulses = {};
                const stamp = (id, count) => {
                    next[id] = count;
                    if (prevCounts.current[id] !== undefined && count > prevCounts.current[id]) {
                        pulses[id] = true;
                    }
                };
                (d.sources || []).forEach((s) => stamp(`src_${s.key}`, s.count));
                (d.stages || []).forEach((s) => stamp(`st_${s.code}`, s.count));
                Object.entries(d.downstream || {}).forEach(([k, v]) => stamp(`dn_${k}`, v.count));
                if (d.email) {
                    stamp('email_sent', d.email.sent);
                    stamp('email_failed', d.email.failed);
                }

                const hadPrev = Object.keys(prevCounts.current).length > 0;
                const pulseActive = hadPrev && Object.keys(pulses).length > 0;
                prevCounts.current = next;

                // Reconcile into React Flow: keep dragged positions, refresh data + pulse flags.
                const { nodes: desired, edges: desiredEdges } = buildGraph(d, pulseActive ? pulses : {});
                setNodes((prev) => {
                    const prevPos = {};
                    prev.forEach((p) => { prevPos[p.id] = p.position; });
                    return desired.map((n) => (prevPos[n.id] ? { ...n, position: prevPos[n.id] } : n));
                });
                setEdges(desiredEdges);
                setTotals(d.totals || null);
                setHasData(true);
                setError('');

                if (pulseActive) {
                    // Clear the pulse flags shortly after so the glow is a one-shot.
                    setTimeout(() => {
                        const { nodes: calm } = buildGraph(d, {});
                        setNodes((prev) => {
                            const prevPos = {};
                            prev.forEach((p) => { prevPos[p.id] = p.position; });
                            return calm.map((n) => (prevPos[n.id] ? { ...n, position: prevPos[n.id] } : n));
                        });
                    }, 2500);
                    if (soundOnRef.current) {
                        ensureAudio();
                        playBeep(Boolean(pulses.email_failed));
                    }
                }
            })
            .catch((err) => setError(err?.response?.data?.message || 'Gagal memuatkan data aliran.'));
    }, [apiUrl, token, ensureAudio, playBeep, setNodes, setEdges]);

    useEffect(() => {
        load();
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [load]);

    useEffect(() => {
        const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener('fullscreenchange', onFs);
        return () => document.removeEventListener('fullscreenchange', onFs);
    }, []);

    const toggleFullscreen = () => {
        const el = containerRef.current;
        if (!document.fullscreenElement && el?.requestFullscreen) {
            el.requestFullscreen().catch(() => {});
        } else if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
        }
    };

    const onNodeClick = useCallback((_e, node) => setSelected(node.data), []);

    return (
        <div className={`aduan-flow${isFullscreen ? ' is-fullscreen' : ''}`} ref={containerRef}>
            <style>{FLOW_CSS}</style>

            <div className="aduan-flow-topbar">
                <div className="aduan-flow-title">
                    <span className="af-eyebrow">Pemantauan Sistem</span>
                    <h3>Aliran Aduan <span className="af-live"><i className="bi bi-broadcast"></i> langsung</span></h3>
                </div>
                <div className="aduan-flow-stats">
                    {totals && (
                        <>
                            <div className="af-stat"><span>Jumlah</span><strong>{totals.total ?? 0}</strong></div>
                            <div className="af-stat af-stat-today"><span>Hari Ini</span><strong>{totals.today ?? 0}</strong></div>
                        </>
                    )}
                    <button
                        type="button"
                        className={`af-btn${soundOn ? ' is-sound-on' : ''}`}
                        onClick={() => {
                            const nextState = !soundOn;
                            setSoundOn(nextState);
                            if (nextState) { ensureAudio(); playBeep(false); }
                        }}
                        title={soundOn ? 'Bunyi: HIDUP (klik untuk matikan)' : 'Bunyi: MATI (klik untuk hidupkan)'}
                        aria-pressed={soundOn}
                    >
                        <i className={`bi ${soundOn ? 'bi-volume-up-fill' : 'bi-volume-mute'}`}></i>
                    </button>
                    <button type="button" className="af-btn" onClick={load} title="Segar semula"><i className="bi bi-arrow-clockwise"></i></button>
                    <button type="button" className="af-btn" onClick={toggleFullscreen} title="Skrin penuh">
                        <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'}`}></i>
                        <span className="af-btn-txt">{isFullscreen ? 'Keluar' : 'Skrin Penuh'}</span>
                    </button>
                </div>
            </div>

            {error && <div className="aduan-flow-error">{error}</div>}

            <div className="aduan-flow-canvas">
                {!hasData ? (
                    <div className="af-loading">Memuatkan aliran…</div>
                ) : (
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onNodeClick={onNodeClick}
                        nodesConnectable={false}
                        edgesFocusable={false}
                        fitView
                        fitViewOptions={{ padding: 0.15 }}
                        minZoom={0.3}
                        maxZoom={1.6}
                        proOptions={{ hideAttribution: true }}
                    >
                        <Background color="#1c2947" gap={22} size={1} />
                        <MiniMap
                            pannable
                            zoomable
                            nodeColor={miniColor}
                            nodeStrokeWidth={0}
                            maskColor="rgba(5,9,20,.7)"
                            style={{ background: '#0b1326', border: '1px solid #1c2947', borderRadius: 10 }}
                        />
                        <Controls showInteractive={false} />
                    </ReactFlow>
                )}

                {selected && (
                    <div className="af-panel">
                        <button type="button" className="af-panel-close" onClick={() => setSelected(null)}><i className="bi bi-x-lg"></i></button>
                        <div className="af-panel-ico"><i className={`bi ${selected.icon}`}></i></div>
                        <div className="af-panel-label">{selected.label}</div>
                        {selected.isEmail ? (
                            <>
                                <div className="af-panel-emrow">
                                    <div className="af-panel-em ok"><span className="n">{selected.sent}</span><span className="t">Berjaya</span></div>
                                    <div className="af-panel-em bad"><span className="n">{selected.failed}</span><span className="t">Gagal</span></div>
                                </div>
                                <div className="af-panel-emmeta">
                                    <span>{selected.rate === null || selected.rate === undefined ? '—' : `${selected.rate}%`} kadar berjaya</span>
                                    {selected.skipped > 0 && <span>{selected.skipped} dilangkau</span>}
                                    <span>Hari ini: {selected.today?.sent ?? 0} ✓ · {selected.today?.failed ?? 0} ✗</span>
                                </div>
                                {selected.failed > 0 && (
                                    <div className="af-panel-note af-panel-note-bad">Ada hantaran e-mel gagal — semak konfigurasi SMTP.</div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="af-panel-count">{selected.count}</div>
                                <div className="af-panel-sub">rekod</div>
                            </>
                        )}
                        {selected.byDistrict && selected.byDistrict.length > 0 && (
                            <div className="af-panel-dist">
                                <div className="af-panel-dist-h">Ikut Daerah</div>
                                <div className="af-panel-dist-list">
                                    {selected.byDistrict.map((d) => (
                                        <div className="af-panel-dist-row" key={d.district}>
                                            <span className="dn" title={d.district}>{d.district}</span>
                                            <span className="dc">{d.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {selected.loose && <div className="af-panel-note">Modul ini tidak berpaut secara automatik dengan aduan (anggaran).</div>}
                        {selected.href && (
                            <button type="button" className="af-panel-link" onClick={() => { const h = selected.href; setSelected(null); if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); navigate(h); }}>
                                Lihat senarai <i className="bi bi-arrow-right"></i>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

const FLOW_CSS = `
.aduan-flow{ background:#070b16; border-radius:16px; padding:14px 14px 16px; color:#e6ecf8; box-shadow:0 20px 50px rgba(5,9,20,.35); }
.aduan-flow.is-fullscreen{ border-radius:0; height:100vh; padding:14px; display:flex; flex-direction:column; }
.aduan-flow-topbar{ display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; padding:4px 4px 12px; }
.aduan-flow-title h3{ margin:2px 0 0; color:#fff; font-size:20px; font-weight:700; display:flex; align-items:center; gap:10px; }
.af-eyebrow{ font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:#8aa0c6; font-weight:700; }
.af-live{ font-size:11px; font-weight:700; color:#34d399; display:inline-flex; align-items:center; gap:5px; padding:3px 9px; border:1px solid rgba(52,211,153,.4); border-radius:20px; background:rgba(52,211,153,.08); }
.af-live i{ animation:afblink 1.6s ease-in-out infinite; }
@keyframes afblink{ 0%,100%{opacity:1;} 50%{opacity:.35;} }
.aduan-flow-stats{ display:flex; align-items:center; gap:10px; }
.af-stat{ background:#0e162b; border:1px solid #1c2947; border-radius:12px; padding:6px 14px; text-align:center; }
.af-stat span{ display:block; font-size:10px; letter-spacing:.08em; text-transform:uppercase; color:#8aa0c6; }
.af-stat strong{ font-size:20px; color:#fff; font-variant-numeric:tabular-nums; }
.af-stat-today strong{ color:#ffd84d; }
.af-btn{ display:inline-flex; align-items:center; gap:8px; background:#132043; color:#cfe0ff; border:1px solid #294066; border-radius:10px; padding:8px 12px; cursor:pointer; font-weight:600; font-size:14px; }
.af-btn:hover{ background:#1a2c57; }
.af-btn.is-sound-on{ background:#163a2e; border-color:#2f6b52; color:#8ef0c2; }
.af-btn.is-sound-on:hover{ background:#1c4a3a; }
.af-btn-txt{ font-size:13px; }
.aduan-flow-error{ margin:0 4px 8px; color:#fca5a5; background:rgba(220,38,38,.12); border:1px solid rgba(220,38,38,.35); border-radius:8px; padding:8px 12px; }
.aduan-flow-canvas{ position:relative; height:calc(100vh - 230px); min-height:520px; border-radius:14px; overflow:hidden; border:1px solid #16213d; background:radial-gradient(circle at 30% 20%, #0c1428, #070b16 70%); }
.aduan-flow.is-fullscreen .aduan-flow-canvas{ flex:1 1 auto; height:auto; }
.af-loading{ display:flex; align-items:center; justify-content:center; height:100%; color:#8aa0c6; }

/* React Flow surface — transparent so the canvas gradient shows through */
.aduan-flow-canvas .react-flow{ background:transparent; }
.aduan-flow-canvas .react-flow__node{ cursor:pointer; }
.aduan-flow-canvas .react-flow__node-flow{ background:none; border:none; padding:0; border-radius:0; width:auto; box-shadow:none; font-family:inherit; }
.aduan-flow-canvas .react-flow__handle.af-h{ opacity:0; width:1px; height:1px; min-width:0; min-height:0; border:none; background:transparent; pointer-events:none; }
.aduan-flow-canvas .react-flow__attribution{ display:none; }
.aduan-flow-canvas .react-flow__controls{ box-shadow:0 8px 22px rgba(0,0,0,.45); border-radius:10px; overflow:hidden; }
.aduan-flow-canvas .react-flow__controls-button{ background:#132043; border-bottom:1px solid #294066; color:#cfe0ff; }
.aduan-flow-canvas .react-flow__controls-button:hover{ background:#1a2c57; }
.aduan-flow-canvas .react-flow__controls-button svg{ fill:#cfe0ff; }

/* edges */
.aduan-flow-canvas .af-rf-edge .react-flow__edge-path{ stroke-linecap:round; }
.aduan-flow-canvas .af-rf-edge.animated .react-flow__edge-path{ stroke-dasharray:9 7; animation:afdash .7s linear infinite; }
.aduan-flow-canvas .af-rf-edge-loose .react-flow__edge-path{ stroke-dasharray:6 7; opacity:.55; animation:none; }
@keyframes afdash{ to{ stroke-dashoffset:-16; } }

/* nodes */
.afn{ position:relative; width:190px; height:${NODE_H}px; box-sizing:border-box; padding:12px 14px; border-radius:14px; background:linear-gradient(160deg,#101a33,#0b1326); border:1px solid #28406e; box-shadow:0 8px 22px rgba(0,0,0,.45); font-family:inherit; }
.afn-row{ display:flex; align-items:center; gap:9px; }
.afn-ico{ width:30px; height:30px; flex:0 0 auto; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:15px; background:rgba(96,165,250,.15); color:#8ab4ff; }
.afn-label{ font-size:12.5px; font-weight:600; line-height:1.15; color:#dbe6fb; }
.afn-count{ margin-top:8px; font-size:26px; font-weight:800; line-height:1; color:#fff; font-variant-numeric:tabular-nums; }
.afn-tag{ position:absolute; top:8px; right:10px; font-size:9px; letter-spacing:.06em; text-transform:uppercase; color:#9fb0cc; border:1px dashed #5a6b8c; border-radius:20px; padding:1px 7px; }
.afn-tag-dist{ border-style:solid; border-color:#2f6b8c; color:#8ecbff; }
.afn-source{ border-color:#1d6b78; }
.afn-source .afn-ico{ background:rgba(34,211,238,.16); color:#44dbef; }
.afn-final{ border-color:#1f7a53; }
.afn-final .afn-ico{ background:rgba(52,211,153,.16); color:#4ade9e; }
.afn-final .afn-count{ color:#6ef0b4; }
.afn-kiv{ border-color:#7a5a1f; }
.afn-kiv .afn-ico{ background:rgba(245,158,11,.16); color:#f6b44b; }
.afn-kes{ border-color:#8a6a1b; background:linear-gradient(160deg,#1b1606,#0f0c04); }
.afn-kes .afn-ico{ background:rgba(251,191,36,.18); color:#ffd164; }
.afn-kes .afn-count{ color:#ffd84d; }
.afn-loose{ border-style:dashed; border-color:#4a5873; opacity:.9; }
.afn-loose .afn-ico{ background:rgba(148,163,184,.15); color:#aebbd0; }
.afn-email{ height:118px; border-color:#2a5a8a; }
.afn-email.afn-email-bad{ border-color:#7a2f33; background:linear-gradient(160deg,#1c1012,#120a0b); }
.afn-email.afn-email-bad .afn-ico{ background:rgba(248,113,113,.16); color:#f87171; }
.afn-email-stats{ display:flex; gap:8px; margin-top:8px; }
.afn-em{ flex:1 1 0; display:flex; align-items:center; justify-content:center; gap:6px; padding:5px 4px; border-radius:9px; font-size:17px; font-weight:800; font-variant-numeric:tabular-nums; }
.afn-em i{ font-size:11px; }
.afn-em-ok{ background:rgba(52,211,153,.12); color:#5ff0b0; border:1px solid rgba(52,211,153,.3); }
.afn-em-bad{ background:rgba(248,113,113,.12); color:#ff9b9b; border:1px solid rgba(248,113,113,.3); }
.afn-email-rate{ margin-top:6px; font-size:10.5px; letter-spacing:.03em; color:#9fb0cc; text-align:center; }
.afn-pulse{ animation:afnpulse 1.1s ease-out 2; }
@keyframes afnpulse{ 0%{ box-shadow:0 0 0 0 rgba(255,216,77,.55), 0 8px 22px rgba(0,0,0,.4); border-color:#ffd84d; } 100%{ box-shadow:0 0 0 18px rgba(255,216,77,0), 0 8px 22px rgba(0,0,0,.4); } }

/* detail panel */
.af-panel{ position:absolute; top:16px; right:16px; width:240px; z-index:20; background:#0d1529; border:1px solid #28406e; border-radius:14px; padding:18px; box-shadow:0 18px 40px rgba(0,0,0,.5); text-align:center; }
.af-panel-close{ position:absolute; top:8px; right:8px; background:transparent; border:none; color:#8aa0c6; cursor:pointer; font-size:13px; }
.af-panel-ico{ width:46px; height:46px; margin:4px auto 10px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:20px; background:rgba(96,165,250,.16); color:#8ab4ff; }
.af-panel-label{ font-size:14px; font-weight:700; color:#eaf1ff; }
.af-panel-count{ font-size:34px; font-weight:800; color:#fff; margin-top:6px; font-variant-numeric:tabular-nums; }
.af-panel-sub{ font-size:11px; letter-spacing:.1em; text-transform:uppercase; color:#8aa0c6; }
.af-panel-note{ margin-top:10px; font-size:11px; color:#c9b27a; background:rgba(201,178,122,.1); border:1px solid rgba(201,178,122,.3); border-radius:8px; padding:7px 9px; }
.af-panel-emrow{ display:flex; gap:10px; margin-top:12px; }
.af-panel-em{ flex:1 1 0; border-radius:12px; padding:10px 6px; display:flex; flex-direction:column; align-items:center; gap:2px; }
.af-panel-em .n{ font-size:28px; font-weight:800; font-variant-numeric:tabular-nums; }
.af-panel-em .t{ font-size:10px; letter-spacing:.08em; text-transform:uppercase; }
.af-panel-em.ok{ background:rgba(52,211,153,.1); border:1px solid rgba(52,211,153,.3); color:#6ef0b4; }
.af-panel-em.bad{ background:rgba(248,113,113,.1); border:1px solid rgba(248,113,113,.3); color:#ff9b9b; }
.af-panel-emmeta{ margin-top:10px; display:flex; flex-direction:column; gap:3px; font-size:11px; color:#9fb0cc; }
.af-panel-note-bad{ color:#ffb4b4; background:rgba(248,113,113,.1); border-color:rgba(248,113,113,.35); }
.af-panel-dist{ margin-top:14px; text-align:left; }
.af-panel-dist-h{ font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:#8aa0c6; margin-bottom:6px; }
.af-panel-dist-list{ max-height:190px; overflow-y:auto; display:flex; flex-direction:column; gap:3px; }
.af-panel-dist-row{ display:flex; align-items:center; justify-content:space-between; gap:8px; padding:5px 9px; background:#0e1730; border:1px solid #1d2a49; border-radius:8px; }
.af-panel-dist-row .dn{ font-size:12px; color:#cdd9f2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.af-panel-dist-row .dc{ font-size:13px; font-weight:700; color:#fff; font-variant-numeric:tabular-nums; flex:0 0 auto; }
.af-panel-link{ margin-top:14px; width:100%; display:inline-flex; align-items:center; justify-content:center; gap:7px; background:#1b2c57; color:#dbe6fb; border:1px solid #2f4a7d; border-radius:10px; padding:9px 12px; cursor:pointer; font-weight:600; font-size:13px; }
.af-panel-link:hover{ background:#24386b; }
@media (prefers-reduced-motion: reduce){ .afn-pulse,.af-rf-edge .react-flow__edge-path,.af-live i{ animation:none; } }
`;
